/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Info, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ToastVariant = 'success' | 'warning' | 'error' | 'info';

export interface ToastInput {
  title?: string;
  description?: string;
  variant?: ToastVariant;
  duration?: number;
}

interface ToastEntry extends ToastInput {
  id: number;
  variant: ToastVariant;
}

interface ToastContextValue {
  toast: (t: ToastInput) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

const variantConfig: Record<
  ToastVariant,
  { icon: typeof CheckCircle2; color: string; ring: string }
> = {
  success: { icon: CheckCircle2, color: 'text-secondary-600', ring: 'border-secondary-500/30' },
  warning: { icon: AlertTriangle, color: 'text-warning-500', ring: 'border-warning-500/30' },
  error: { icon: XCircle, color: 'text-error-500', ring: 'border-error-500/30' },
  info: { icon: Info, color: 'text-primary-600', ring: 'border-primary-500/30' },
};

interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [items, setItems] = useState<ToastEntry[]>([]);

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (t: ToastInput) => {
      const id = Date.now() + Math.floor(Math.random() * 1000);
      const entry: ToastEntry = { ...t, id, variant: t.variant ?? 'info' };
      setItems((prev) => [...prev, entry]);
      const duration = t.duration ?? 4500;
      if (duration > 0) {
        window.setTimeout(() => dismiss(id), duration);
      }
      return id;
    },
    [dismiss],
  );

  const value = useMemo<ToastContextValue>(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="pointer-events-none fixed bottom-24 right-24 z-[--z-toast] flex w-full max-w-sm flex-col gap-12"
      >
        {items.map((t) => {
          const cfg = variantConfig[t.variant];
          const Icon = cfg.icon;
          return (
            <div
              key={t.id}
              role="status"
              className={cn(
                'pointer-events-auto flex items-start gap-12 rounded-lg border bg-neutral-0 p-16 shadow-lg',
                cfg.ring,
              )}
            >
              <Icon className={cn('mt-2 h-20 w-20 shrink-0', cfg.color)} aria-hidden="true" />
              <div className="min-w-0 flex-1">
                {t.title && <p className="text-base font-semibold text-neutral-900">{t.title}</p>}
                {t.description && (
                  <p className="mt-2 break-words text-sm text-neutral-600">{t.description}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => dismiss(t.id)}
                className="rounded-sm p-2 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-700"
                aria-label="Dismiss notification"
              >
                <X className="h-16 w-16" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

// Re-export for completeness
export { ToastContext };
